/* Altitude Fans — product-card swatch correction (2026-08-10, ct-1786478400)
 * + instant image swap & preloading (2026-08-31, ct-1788205500)
 *
 * Part 1 (08-10): sparse-matrix repair.
 * The theme builds a variant title by joining the active swatch of every option group
 * ("60 in / Brushed Nickel / Silver") and then requires an exact match on variant.title.
 * Two products have a sparse matrix, so some combinations name a variant that does not
 * exist and the click silently does nothing:
 *
 *   gossamer-ceiling-fan            Size(2) x Finish(3) = 6 possible, 4 real
 *   tritour-ceiling-fan-with-light  Size(2) x Finish(4) = 8 possible, 6 real
 *
 * This listens in the CAPTURE phase, so it runs before wpbingo's bubble-phase jQuery
 * handler. When the combination the shopper is about to select does not exist, it moves
 * the other option groups onto the nearest value that does exist, so wpbingo then reads a
 * selection that resolves. It also keeps unreachable swatches marked `disabled`.
 *
 * Part 2 (08-31): swatch clicks felt slow because wpbingo re-fetches /products/<handle>.js
 * on EVERY click and only then swaps the image — two sequential network hops before any
 * pixel changes. Now every swatch card's product JSON is prefetched once, the _600x
 * variant images are warmed during browser idle time, and the visible image is swapped
 * synchronously from cache in the capture phase. wpbingo's own handler still runs and
 * sets the same src (a no-op) plus price/variant-id, so nothing else changes.
 */
(function () {
  'use strict';

  var cache = {};       // handle -> product JSON
  var pending = {};     // handle -> true while in flight
  var warmed = {};      // image src -> true once preloaded

  function groups(root) {
    return Array.prototype.slice.call(root.querySelectorAll('.swatch-content'));
  }

  function valueOf(group) {
    var active = group.querySelector('.swatch-items.active label');
    return active ? active.getAttribute('data-variant') : null;
  }

  function valuesOf(group) {
    return Array.prototype.slice.call(group.querySelectorAll('.swatch-items label'))
      .map(function (l) { return l.getAttribute('data-variant'); });
  }

  // Does an available variant exist for this exact ordered combination?
  function exists(product, combo) {
    var title = combo.join(' / ');
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      if (v.available && v.title === title) return true;
    }
    return false;
  }

  // Is `value` in group `idx` reachable at all, holding nothing else fixed?
  function reachable(product, idx, value) {
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      if (v.available && v.options[idx] === value) return true;
    }
    return false;
  }

  // Same URL shape wpbingo's click handler builds — must match so its later set is a no-op.
  function sizedSrc(variant) {
    if (!variant || !variant.featured_media || !variant.featured_media.preview_image) return null;
    return variant.featured_media.preview_image.src
      .replace(/(\.[^.]*)$/, '_600x$1').replace('http:', '');
  }

  function warm(src) {
    if (!src || warmed[src]) return;
    warmed[src] = true;
    var img = new Image();
    img.src = src;
  }

  // Warm this product's variant renditions one idle slice at a time.
  function warmProduct(product) {
    var srcs = product.variants.map(sizedSrc).filter(Boolean);
    function step() {
      var next = srcs.shift();
      if (!next) return;
      warm(next);
      if (window.requestIdleCallback) requestIdleCallback(step, { timeout: 4000 });
      else setTimeout(step, 250);
    }
    step();
  }

  function prefetch(root) {
    var handle = root.getAttribute('data-handle');
    if (!handle || cache[handle] || pending[handle]) return;
    pending[handle] = true;
    fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (p && p.variants) { cache[handle] = p; adopt(root); repaint(root); warmProduct(p); flipToFilter(root); }
        delete pending[handle];
      })
      .catch(function () { delete pending[handle]; });
  }

  // Once this product's JSON is cached, OUR capture-phase handler owns the image swap.
  // wpbingo's per-click getJSON still updates price/variant-id, but its image write races:
  // a stale response from click N can land after the instant swap for click N+1 and put the
  // wrong finish back. Renaming the `variant` hook to `af-swap` blinds wpbingo's
  // $("img.variant") selector, so the only image writes left are synchronous and ordered.
  function adopt(root) {
    var card = root.closest('.product-card');
    if (!card) return;
    Array.prototype.slice.call(card.querySelectorAll('.product-card__image img.variant')).forEach(function (img) {
      img.classList.remove('variant');
      img.classList.add('af-swap');
    });
  }

  // Grey out swatch values that no available variant can reach (multi-group only).
  function repaint(root) {
    var product = cache[root.getAttribute('data-handle')];
    if (!product || groups(root).length < 2) return;
    groups(root).forEach(function (group, idx) {
      Array.prototype.slice.call(group.querySelectorAll('.swatch-items')).forEach(function (item) {
        var label = item.querySelector('label');
        if (!label) return;
        var ok = reachable(product, idx, label.getAttribute('data-variant'));
        item.classList.toggle('disabled', !ok);
      });
    });
  }

  function setActive(group, value) {
    Array.prototype.slice.call(group.querySelectorAll('.swatch-items')).forEach(function (item) {
      var label = item.querySelector('label');
      item.classList.toggle('active', !!label && label.getAttribute('data-variant') === value);
    });
  }

  // Swap the card images right now from the cached product, so the click feels instant.
  // Mirrors wpbingo's non-slider branch only; slider cards keep their swiper flow.
  // The HOVER image (.product-card__img-hidden) must be swapped too: while the pointer is
  // on the card (always true when clicking a swatch) the theme paints it at opacity 1 ON
  // TOP of the visible image, so a swap underneath only appears after mouseleave.
  function swapNow(root, product, combo) {
    var card = root.closest('.product-card');
    if (!card) return;
    var wrapper = card.querySelector('.product-card__image-wrapper');
    if (wrapper && wrapper.classList.contains('slider')) return;
    var title = combo.join(' / ');
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      if (v.available && v.title === title) {
        var src = sizedSrc(v);
        if (!src) return;
        warm(src);
        var imgs = card.querySelectorAll('.product-card__image img.af-swap, .product-card__image img.variant, .product-card__image img.product-card__img-hidden');
        Array.prototype.slice.call(imgs).forEach(function (img) {
          img.setAttribute('src', src);
          img.setAttribute('srcset', src);
        });
        return;
      }
    }
  }

  document.addEventListener('click', function (event) {
    var item = event.target.closest && event.target.closest('.swatch-items');
    if (!item) return;
    var root = item.closest('.wpb-variants-swatch');
    if (!root) return;

    var product = cache[root.getAttribute('data-handle')];
    if (!product) { prefetch(root); return; }   // nothing we can do this click; theme behaves as before

    var all = groups(root);
    var clickedGroup = item.closest('.swatch-content');
    var clickedIdx = all.indexOf(clickedGroup);
    var label = item.querySelector('label');
    if (clickedIdx < 0 || !label) return;
    var clickedValue = label.getAttribute('data-variant');

    // What the selection will be once the theme marks this swatch active.
    var combo = all.map(function (g, i) { return i === clickedIdx ? clickedValue : valueOf(g); });
    if (combo.indexOf(null) !== -1) return;

    if (!exists(product, combo) && all.length >= 2) {
      // Dead combination — walk the other groups onto a value that resolves.
      for (var i = 0; i < all.length && !exists(product, combo); i++) {
        if (i === clickedIdx) continue;
        var candidates = valuesOf(all[i]);
        for (var c = 0; c < candidates.length; c++) {
          var trial = combo.slice();
          trial[i] = candidates[c];
          if (exists(product, trial)) { combo = trial; setActive(all[i], candidates[c]); break; }
        }
      }
    }
    repaint(root);
    swapNow(root, product, combo);
  }, true);   // capture: run before wpbingo's handler

  // Part 3 (09-01): when the Color facet is active, flip each card to a variant IN that
  // color. The filter is product-level ("offered in this finish"), so Shopify keeps the
  // default-finish photo on the card — a shopper filtering "Matte White" then sees black
  // fans. The card's finish value whose FIRST slash-segment equals a selected color (same
  // rule that tagged the products) gets a programmatic click, which reuses the whole swap
  // pipeline (instant image, price, variant id). Flipped once per rendered card; the
  // shopper can still change swatches freely afterwards.
  function selectedColors() {
    var out = [];
    new URLSearchParams(location.search).forEach(function (v, k) {
      if (k === 'filter.p.m.specs.finish_colors') out.push(v);
    });
    return out;
  }

  // Self-contained (no synthetic click): at DOMContentLoaded the theme's jQuery handlers
  // are not yet bound, so a click() would land on nothing. Runs once per rendered card,
  // only after this product's JSON is cached.
  function flipToFilter(root) {
    if (root.getAttribute('data-af-color-flipped')) return;
    var colors = selectedColors();
    if (!colors.length) return;
    var product = cache[root.getAttribute('data-handle')];
    if (!product) return;                      // retried from prefetch's then()
    var all = groups(root);
    for (var g = 0; g < all.length; g++) {
      var items = all[g].querySelectorAll('.swatch-items');
      for (var i = 0; i < items.length; i++) {
        var label = items[i].querySelector('label');
        if (!label) continue;
        var value = label.getAttribute('data-variant') || '';
        if (colors.indexOf(value.split('/')[0].trim()) === -1) continue;
        if (items[i].classList.contains('active')) { root.setAttribute('data-af-color-flipped', '1'); return; }
        // Resolve the would-be combo BEFORE committing: if no AVAILABLE variant carries this
        // finish (e.g. Troy's Matte White is sold out) the card must stay on its default —
        // flipping the swatch without an image would show a white swatch on a black fan.
        var combo = all.map(function (grp, idx) { return idx === g ? value : valueOf(grp); });
        if (!exists(product, combo) && all.length >= 2) {
          for (var j = 0; j < all.length && !exists(product, combo); j++) {
            if (j === g) continue;
            var candidates = valuesOf(all[j]);
            for (var c = 0; c < candidates.length; c++) {
              var trial = combo.slice();
              trial[j] = candidates[c];
              if (exists(product, trial)) { combo = trial; break; }
            }
          }
        }
        if (!exists(product, combo)) continue;   // this color is sold out here — try another match
        root.setAttribute('data-af-color-flipped', '1');
        combo.forEach(function (val, idx) { setActive(all[idx], val); });
        repaint(root);
        swapNow(root, product, combo);
        // keep the add-to-cart form pointing at the shown variant (what wpbingo's click does)
        var title = combo.join(' / ');
        for (var v = 0; v < product.variants.length; v++) {
          if (product.variants[v].available && product.variants[v].title === title) {
            var card = root.closest('.product-card');
            if (card) Array.prototype.slice.call(card.querySelectorAll(".product-card__form input[name='id']")).forEach(function (inp) {
              inp.value = product.variants[v].id;
            });
            break;
          }
        }
        return;
      }
    }
    root.setAttribute('data-af-color-flipped', '1');   // no matching swatch on this card
  }

  function init() {
    Array.prototype.slice.call(document.querySelectorAll('.wpb-variants-swatch')).forEach(function (root) {
      prefetch(root);
      flipToFilter(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Collection filtering and infinite scroll swap cards in without a page load.
  new MutationObserver(function () { init(); })
    .observe(document.documentElement, { childList: true, subtree: true });

  // Facet AJAX updates the URL via history.pushState — sometimes AFTER the grid has
  // re-rendered, so the fresh cards were inited against the OLD query string. Re-run init
  // shortly after any history write so the color-filter flip sees the new URL.
  ['pushState', 'replaceState'].forEach(function (fn) {
    var orig = history[fn];
    history[fn] = function () {
      var r = orig.apply(this, arguments);
      setTimeout(init, 80); setTimeout(init, 700);
      return r;
    };
  });
  window.addEventListener('popstate', function () { setTimeout(init, 80); });
})();
